import { yupResolver } from "@hookform/resolvers/yup";
import { BonusCardType } from "@omni/types/enums/bonusCardType.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { isDefined } from "@omni/utils/helper.utils";
import React, { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";

import { Label } from "@/components/Form/Base/label.component";
import { NumericTextField } from "@/components/Form/RHF/FormFields/NumericTextField/NumericTextfield.component";
import { PromotionField } from "@/components/Form/RHF/FormFields/PromotionField/promotionField.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { getCart } from "@/redux/modules/api/data/cart/cart.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import * as BonusCardUtils from "@/utils/bonusCard";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { BONUSCARD_DISCOUNT_PREFIX } from "@/utils/validation/config";

import {
    PromotionInputFieldSchema,
    PromotionInputFormValues
} from "./promotionInputField.schema";

const FORM_ID = "giftCardForm";

export type PromotionInputFieldProps = {
    autofocus: boolean;
    addPromotionCode: (code: string, bonusCardNumber?: string) => void;
    loading: boolean;
    setActive: (isActive: boolean) => void;
};

export const PromotionInputField = ({
    autofocus,
    addPromotionCode,
    loading,
    setActive
}: PromotionInputFieldProps) => {
    const { t } = useGranite();
    const cart = useSelector(getCart);
    const textfieldRef = React.createRef<HTMLInputElement>();
    const [hasBonusCardPrefix, setHasBonusCardPrefix] = useState(false);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            codeInput: "",
            bonusCard: cart?.bonusCardNumber
                ? { number: cart.bonusCardNumber }
                : undefined
        },
        resolver: yupResolver(PromotionInputFieldSchema)
    });

    const { subscribe, reset } = methods;

    useEffect(() => {
        const callback = subscribe({
            formState: { values: true },
            callback: ({ values }) => {
                const shouldHaveBonusCardPrefix = values.codeInput.startsWith(
                    BONUSCARD_DISCOUNT_PREFIX
                );
                if (shouldHaveBonusCardPrefix !== hasBonusCardPrefix) {
                    setHasBonusCardPrefix(shouldHaveBonusCardPrefix);
                }
            }
        });

        return () => callback();
    }, [subscribe, hasBonusCardPrefix]);

    useEffect(() => {
        if (!autofocus || !isDefined(textfieldRef.current)) return;

        textfieldRef.current.focus();
    }, [textfieldRef.current, autofocus]);

    const onFormSubmit = (values: PromotionInputFormValues) => {
        if (values.codeInput.trim().length === 0) {
            setActive(false);
        } else {
            let bonusCardNumber: string | undefined = values.bonusCard?.number;
            if (!values.codeInput.startsWith(BONUSCARD_DISCOUNT_PREFIX)) {
                bonusCardNumber = undefined;
            }
            addPromotionCode(values.codeInput, bonusCardNumber);
        }

        reset(undefined, { keepDefaultValues: true });
    };

    const isEBonusCardNumber = BonusCardUtils.isType(
        BonusCardType.Electronic,
        cart?.bonusCardNumber
    );

    return (
        <div className="discount-form-wrapper">
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <FormProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onFormSubmit)}
                        >
                            <Row>
                                <Col>
                                    <PromotionField<PromotionInputFormValues>
                                        label={""}
                                        hideMicroCopy={true}
                                        hideLabel={true}
                                        name={"codeInput"}
                                        placeholder={t(
                                            "checkout.discount.placeholder"
                                        )}
                                        required
                                        ref={textfieldRef}
                                    />
                                </Col>
                            </Row>
                            {hasBonusCardPrefix && (
                                <>
                                    {!isEBonusCardNumber && (
                                        <Row>
                                            <Col>
                                                <NumericTextField<PromotionInputFormValues>
                                                    name="bonusCard.number"
                                                    label={t(
                                                        "general.form.bonusCard.label"
                                                    )}
                                                    placeholder={t(
                                                        "general.form.bonusCard.placeholder"
                                                    )}
                                                    required={true}
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                    {isEBonusCardNumber && (
                                        <Row>
                                            <Col>
                                                <Label>
                                                    {t(
                                                        "general.form.bonusCard.label"
                                                    )}
                                                </Label>
                                                <span className="pl-1">
                                                    {cart?.bonusCardNumber}
                                                </span>
                                            </Col>
                                        </Row>
                                    )}
                                </>
                            )}
                            <Row>
                                <Col>
                                    <Button
                                        test="promotionInput-button-submit"
                                        type="submit"
                                        loading={loading}
                                    >
                                        {t("checkout.discount.label.activate")}
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
        </div>
    );
};
